import React from 'react';
import { LAUNCH_LABELS, formatLaunches, type ElevationPoint } from '../../data/itinerary-mock';

interface LaunchChartProps {
  title: string;
  bars: number[];
  total: number;
}

export function LaunchChart({ title, bars, total }: LaunchChartProps) {
  const peak = Math.max(4, ...bars, 1);
  const mid = Math.round(peak / 2);

  return (
    <section className="it-chartcard" aria-label={`${title}, last 30 days`}>
      <div className="it-chartcard__head">
        <h3>
          {title}
          <span> (last 30 days)</span>
        </h3>
        <span className="it-pill">{formatLaunches(total)}</span>
      </div>
      <div className="it-bars">
        <div className="it-bars__axis" aria-hidden="true">
          <span>{peak}</span>
          <span>{mid}</span>
          <span>0</span>
        </div>
        <div className="it-bars__plot">
          {LAUNCH_LABELS.map((label, index) => {
            const value = bars[index] ?? 0;
            const height = value <= 0 ? 0 : Math.max(8, (value / peak) * 100);
            return (
              <div key={label} className="it-bars__col">
                <div className="it-bars__track">
                  <div className="it-bars__bar" style={{ height: `${height}%` }} />
                </div>
                <span>{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ElevationChart({ points }: { points: ElevationPoint[] }) {
  const meters = points.map((point) => point.meters);
  const distances = points.map((point) => point.km);
  let low = Math.min(150, ...meters);
  let high = Math.max(300, ...meters);
  low = Math.floor(low / 50) * 50;
  high = Math.ceil(high / 50) * 50;
  if (high <= low) high = low + 50;
  const maxKm = Math.max(...distances, 0.1);
  const width = 320;
  const height = 148;
  const padL = 36;
  const padR = 8;
  const padT = 12;
  const padB = 24;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  const xAt = (km: number) => padL + (km / maxKm) * innerW;
  const yAt = (value: number) => padT + ((high - value) / (high - low)) * innerH;
  const line = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${xAt(point.km).toFixed(1)} ${yAt(point.meters).toFixed(1)}`)
    .join(' ');
  const area = points.length
    ? `${line} L ${xAt(points[points.length - 1].km).toFixed(1)} ${(padT + innerH).toFixed(1)} L ${xAt(points[0].km).toFixed(1)} ${(padT + innerH).toFixed(1)} Z`
    : '';
  const ticks = [high, Math.round((high + low) / 2), low];
  const xTicks = [0, Math.round((maxKm / 2) * 10) / 10, Math.round(maxKm * 10) / 10];

  return (
    <section className="it-chartcard" aria-label="Elevation profile">
      <div className="it-chartcard__head">
        <h3>
          Elevation profile
          <span> (approx.)</span>
        </h3>
      </div>
      <svg className="it-elev" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Elevation along the route">
        {ticks.map((tick) => (
          <g key={tick}>
            <line className="it-elev__grid" x1={padL} x2={width - padR} y1={yAt(tick)} y2={yAt(tick)} />
            <text className="it-elev__label" x={padL - 6} y={yAt(tick) + 3} textAnchor="end">{tick}</text>
          </g>
        ))}
        {area ? <path className="it-elev__area" d={area} /> : null}
        {line ? <path className="it-elev__line" d={line} /> : null}
        {points.length > 0 ? (
          <circle className="it-elev__dot" cx={xAt(points[points.length - 1].km)} cy={yAt(points[points.length - 1].meters)} r="3.5" />
        ) : null}
        {xTicks.map((tick) => (
          <text key={tick} className="it-elev__xlabel" x={xAt(tick)} y={height - 6} textAnchor="middle">
            {tick} km
          </text>
        ))}
      </svg>
    </section>
  );
}
